import { ProjectRenderer } from '../ProjectRenderer';
import { RenderUtils } from '../utils/RenderUtils';
import { AssetsLib } from '../utils/AssetsLib';
import {
	SerializedDraggableObject,
	UserDataObjectType,
	UserDataProp,
} from '../types';
import { DraggableObject } from './DraggableObject';
import { RotationDirection, UUID } from '@/types/types';
import { Asset } from '@/api/AssetApi';
import { Box3, Vector3 } from 'three';
import { RoomWall } from './RoomWall';
import RENDERER_CONFIG from '@/configs/rendererConfig';

const { LANDSCAPE_MODULE } = RENDERER_CONFIG;

export interface SerializedLandscapeModule extends SerializedDraggableObject {
	adjustableToWalls: boolean;
	hostWallIndex?: number | null;
}

export class LandscapeModule extends DraggableObject {
	static readonly LAYER = LANDSCAPE_MODULE.LAYER;
	static readonly SNAP_TO_WALL_DISTANCE =
		LANDSCAPE_MODULE.SNAP_TO_WALL_DISTANCE;

	private assetsLib = AssetsLib.getInstance();
	private asset: Asset;
	private rawBox = new Box3();
	private disposed = false;
	private readyPromise: Promise<void>;
	private pendingHostWallIndex: number | null | undefined;

	hostWall: RoomWall | null = null;
	adjustableToWalls: boolean;

	constructor(
		renderer: ProjectRenderer,
		id: UUID,
		asset: Asset,
		adjustableToWalls: boolean,
		config?: SerializedLandscapeModule
	) {
		super(renderer);

		this.id = id;
		this.asset = asset;
		this.adjustableToWalls = adjustableToWalls;

		this.renderer.room.add(this.group);

		this.readyPromise = this.initialize(config);
	}

	get ready() {
		return this.readyPromise;
	}

	get position() {
		return this.group.position;
	}

	set position(value: Vector3) {
		this.group.position.copy(value);
		this.updateBox();
	}

	private async initialize(config?: SerializedLandscapeModule) {
		await this.loadAsset(this.asset.model!.path);
		if (this.disposed) return;

		if (config) this.loadFromData(config);
		if (this.disposed) return;

		this.resolveHostWall();
	}

	private async loadAsset(src: string) {
		const model = await this.assetsLib.getModel(src);
		if (this.disposed) return;

		model.traverse((child) => {
			child.userData[UserDataProp.OBJECT_TYPE] =
				UserDataObjectType.LANDSCAPE_MODULE;
			child.userData[UserDataProp.OBJECT_ID] = this.id;
			child.layers.set(LandscapeModule.LAYER);
		});

		this.group.add(model);

		this.updateBox();
		if (import.meta.env.DEV) this.renderer.scene.add(this.boxHelper);
	}

	protected override loadFromData<T extends SerializedDraggableObject>(
		data: T
	) {
		super.loadFromData(data);
		this.updateBox();

		if ('hostWallIndex' in data)
			this.pendingHostWallIndex =
				data.hostWallIndex as SerializedLandscapeModule['hostWallIndex'];
		else this.pendingHostWallIndex = undefined;
	}

	protected override updateBox() {
		this.rawBox.setFromObject(this.group);
		this.box.setFromObject(this.group);

		const size = this.box.getSize(new Vector3());
		const maxDim = Math.max(size.x, size.y, size.z);

		const center = this.box.getCenter(new Vector3());

		this.box.copy(
			new Box3().setFromCenterAndSize(
				center,
				new Vector3(maxDim, maxDim, maxDim)
			)
		);

		if (this.adjustableToWalls)
			this.box.expandByScalar(LandscapeModule.SNAP_TO_WALL_DISTANCE);
	}

	private resolveHostWall() {
		if (!this.adjustableToWalls) {
			this.hostWall = null;
			return;
		}

		if (this.pendingHostWallIndex !== undefined) {
			if (this.pendingHostWallIndex === null) {
				this.hostWall = null;
				return;
			}

			this.hostWall = this.renderer.roomManager.getWallByIndex(
				this.pendingHostWallIndex
			);
			return;
		}

		const intersectedWalls =
			this.renderer.roomManager.getIntersectedWalls(this);
		this.hostWall = intersectedWalls[0] ?? null;
	}

	snapToWall(wall: RoomWall) {
		this.hostWall = wall;
		const snapedPosition = this.position.clone();

		const wallAxis = wall.getWallAxis();

		if (wallAxis === 'x') snapedPosition.z = wall.position.y;
		else snapedPosition.x = wall.position.x;

		this.position = snapedPosition;

		const isParallelToWall = RenderUtils.areBoxesParallel(
			this.rawBox,
			wall.box
		);
		if (!isParallelToWall) this.rotate(RotationDirection.CLOCKWISE);
	}

	dispose() {
		this.disposed = true;
		RenderUtils.disposeObject(this.group);
	}

	export(): SerializedLandscapeModule {
		return {
			id: this.id,
			asset: this.asset,
			position: this.group.position.toArray(),
			rotation: this.group.rotation.y,
			adjustableToWalls: this.adjustableToWalls,
			hostWallIndex: this.hostWall
				? this.renderer.roomManager.getWallIndex(this.hostWall)
				: null,
		};
	}
}
